import React from 'react';

import { ErrorModel } from 'common/api/autogenerated/client-area';

import { useI18n } from 'common/hooks';

import { memoizedComponent } from 'utils/memoizeComponent';

import './ServerError.css';

export type ServerErrorType = ErrorModel | undefined;

type ErrorProps = {
  error?: ServerErrorType | string;
  className?: string;
};

const replaceExternalErrorCode = (error = '') => {
  const isInternalServiceError = /:/gm.test(error);
  if (isInternalServiceError) {
    const [, ...rest] = error.split(':') || [];
    return [rest.join(':')];
  }
  return error;
};

export const composeServerError = ({ body: error }: { body: ServerErrorType }, errorCodes: Record<string, string>) => {
  const result: ErrorModel = {};

  if (typeof error === 'string') {
    result.message = error;
    return result;
  }

  if (error?.code) result.code = errorCodes[error.code] || error.code;

  if (error?.message) result.message = error?.message;

  return result;
};

export const ServerError: React.FC<React.PropsWithChildren<ErrorProps>> = memoizedComponent(
  ({ error, className = '' }) => {
    const { translate } = useI18n();

    const err = React.useMemo(() => {
      if (typeof error === 'string') return [replaceExternalErrorCode(error)];

      if (error?.code || error?.message || error?.errors) {
        const { code = '', message = '', errors } = error;

        const details = errors && typeof errors === 'object' ? Object.values(errors).flat() : [];

        if (details.length) return details;

        if (code) {
          const _translatedCode = translate.toText(`mobile:errors:${code}`, true);
          if (_translatedCode) return [_translatedCode];
        }

        if (message) {
          return [replaceExternalErrorCode(message)];
        }
      }

      return [translate.toText('mobile:errors:unknown')];
    }, [error, translate]);

    if (!error) return <p data-test='server-error-empty' className={`server-error ${className}`} />;

    return (
      <>
        {err.map((errorMessage, index) => (
          <p key={index} data-test='server-error' className={`server-error ${className}`}>
            {errorMessage}
          </p>
        ))}
      </>
    );
  },
);
